import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSiteToday } from '@d-orca/shared/client/hooks';
import type { IEquipmentMaintenanceDraft } from '@d-orca/shared/equipment-maintenance';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useCanAccess } from '@permissions/useFeatures';
import { Button } from '@components/ui/Button/Button';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { PlusIcon, WrenchIcon } from '@utils/icons';
import { useToast } from '@components/ui/Toasts/toastContext';
import detailStyles from '../AssetDetail.module.css';
import styles from './MaintenanceSchedule.module.css';
import { MaintenanceUpcomingList, type OpenEntry } from './MaintenanceUpcomingList/MaintenanceUpcomingList';
import { MaintenanceLog } from './MaintenanceLog/MaintenanceLog';
import { MaintenanceDialog } from './MaintenanceDialog/MaintenanceDialog';
import { maintenanceDay } from './maintenanceDay';
import {
  useEquipmentMaintenance,
  useEquipmentMaintenanceLog,
  useEquipmentMaintenanceWrites,
} from '../../hooks/useEquipmentMaintenance';

interface IMaintenanceScheduleProps {
  siteId: string | null;
  equipmentId: string;
}

/** The asset's maintenance: what is coming up, and what was done.
 *  Waits for the site and its zone: urgency is judged on the site's today. */
export function MaintenanceSchedule({ siteId, equipmentId }: IMaintenanceScheduleProps) {
  const tz = useSelector(selectSiteTz);
  const today = useSiteToday(tz);
  return siteId && tz ? <Schedule siteId={siteId} equipmentId={equipmentId} tz={tz} today={today} /> : null;
}

interface IScheduleProps {
  siteId: string;
  equipmentId: string;
  tz: string;
  today: string;
}

function Schedule({ siteId, equipmentId, tz, today }: IScheduleProps) {
  const { t } = useTranslation();
  const canEdit = useCanAccess('admin');
  const schedule = useEquipmentMaintenance(siteId, equipmentId);
  const log = useEquipmentMaintenanceLog(siteId, equipmentId);
  const writes = useEquipmentMaintenanceWrites(siteId, equipmentId);
  const pushToast = useToast();
  // `null` adds; an entry edits it.
  const [editing, setEditing] = useState<OpenEntry | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<OpenEntry | null>(null);
  const close = () => setEditing(undefined);

  const save = (draft: IEquipmentMaintenanceDraft) => {
    if (editing) writes.update.mutate({ id: editing.id, title: draft.title }, { onSuccess: close });
    else writes.add.mutate(draft, { onSuccess: close });
  };

  const markDone = (entry: OpenEntry) => {
    // One at a time: a second click before the first lands would only be refused.
    if (writes.markDone.isPending) return;
    writes.markDone.mutate(entry.id, {
      onSuccess: (after) => pushToast({
        kind: 'success',
        title: t('equipment.detail.maintenance.doneToast', { title: entry.title }),
        sub: after.nextDueDate
          ? t('equipment.detail.maintenance.doneToastNext', { date: maintenanceDay(after.nextDueDate, tz, today) })
          : t('equipment.detail.maintenance.doneToastOnce'),
      }),
    });
  };

  const actionsFor = canEdit
    ? (entry: OpenEntry) => ({
      onMarkDone: () => markDone(entry),
      onEdit: () => setEditing(entry),
      onDelete: () => setDeleting(entry),
    })
    : undefined;

  return (
    <section className={detailStyles.section}>
      <div className={styles.header}>
        <div className={detailStyles.sectionTitleRow}>
          <span className={styles.titleIcon}><WrenchIcon size={13} /></span>
          <h4 className={detailStyles.sectionTitle}>{t('equipment.detail.maintenanceSchedule')}</h4>
        </div>
        {canEdit && (
          <Button variant="ghost" size="sm" className={styles.add} onClick={() => setEditing(null)}>
            {t('equipment.detail.maintenance.add')}
            <PlusIcon size={12} />
          </Button>
        )}
      </div>
      <MaintenanceUpcomingList
        entries={schedule.data}
        isPending={schedule.isPending}
        isError={schedule.isError}
        today={today}
        tz={tz}
        actionsFor={actionsFor}
      />
      {log.isError && <p className={styles.note}>{t('equipment.detail.maintenance.logUnavailable')}</p>}
      {log.data && log.data.length > 0 && <MaintenanceLog records={log.data} tz={tz} today={today} />}

      {editing !== undefined && (
        <MaintenanceDialog
          initial={editing}
          today={today}
          onClose={close}
          onSave={save}
          saving={writes.add.isPending || writes.update.isPending}
        />
      )}

      {deleting && (
        <DiscardModal
          open
          title={t('equipment.detail.maintenance.deleteTitle')}
          description={t('equipment.detail.maintenance.deleteDescription', { title: deleting.title })}
          loading={writes.remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => writes.remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
        />
      )}
    </section>
  );
}
